import Vue from 'vue';
import {cloneDeep as _cloneDeep} from 'lodash';
import ReportModel, {
    defaultBuyer,
    defaultDeposit,
    defaultExclusive,
    defaultOwnerMeeting,
    defaultPlannedTask,
    defaultShowing
} from "@/models/ReportModel"

const ReportsModule = {
    namespaced: true,
    state: () => ({
        // Список звітів для таблиці
        reports: [],
        // Поточний звіт для форм
        report: _cloneDeep(ReportModel),
        // Загальна кількість звітів для пагінації
        reportsCount: 0,
        // Стан наявності сьогоднішнього звіту для поточного користувача
        todayReportExists: false,
        // Список користувачів для фільтрації (для адміністраторів)
        allUsers: [],
        // Параметри фільтрації
        filters: {
            date_from: null,
            date_to: null,
            date: null,
            status: null,
            agent_id: null,
            sort_by: 'report_date',
            sort_dir: 'desc',
            per_page: 30,
            page: 1
        }
    }),
    
    getters: {
        getReports(state) {
            return state.reports
        },
        getReport(state) {
            return state.report
        },
        getReportsCount(state) {
            return state.reportsCount
        },
        getTodayReportExists(state) {
            return state.todayReportExists
        },
        getAllUsers(state) {
            return state.allUsers
        },
        getFilters(state) {
            return state.filters
        }
    },
    
    mutations: {
        setReports(state, payload) {
            state.reports = payload
        },
        setReport(state, payload) {
            state.report = payload
        },
        setReportsCount(state, payload) {
            state.reportsCount = payload
        },
        setTodayReportExists(state, payload) {
            state.todayReportExists = !!payload
        },
        setAllUsers(state, payload) {
            state.allUsers = payload
        },
        setFilters(state, payload) {
            state.filters = { ...state.filters, ...payload }
        },
        resetFilters(state) {
            state.filters = {
                ...state.filters,
                date_from: null,
                date_to: null,
                date: null,
                status: null,
                agent_id: null,
                sort_by: 'report_date',
                sort_dir: 'desc',
                page: 1
            }
        },
        addOwnerMeeting(state) {
            if (!state.report.owner_meetings) {
                state.report.owner_meetings = []
            }
            state.report.owner_meetings.push(_cloneDeep(defaultOwnerMeeting))
        },
        removeOwnerMeeting(state, index) {
            if (state.report.owner_meetings && state.report.owner_meetings[index]) {
                state.report.owner_meetings.splice(index, 1)
            }
        },
        addShowing(state) {
            if (!state.report.showings) {
                state.report.showings = []
            }
            state.report.showings.push(_cloneDeep(defaultShowing))
            if (!state.report.showings_summary) {
                state.report.showings_summary = { showings_count: 0, clients_count: 0 }
            }
            state.report.showings_summary.showings_count = state.report.showings.length
        },
        removeShowing(state, index) {
            if (state.report.showings && state.report.showings[index]) {
                state.report.showings.splice(index, 1)
            }
            if (state.report.showings_summary) {
                state.report.showings_summary.showings_count = (state.report.showings || []).length
            }
        },
        syncShowingsToCount(state, count) {
            const target = Math.max(0, Number(count) || 0)
            if (!state.report.showings) {
                state.report.showings = []
            }
            if (!state.report.showings_summary) {
                state.report.showings_summary = { showings_count: 0, clients_count: 0 }
            }
            state.report.showings_summary.showings_count = target

            while (state.report.showings.length < target) {
                state.report.showings.push(_cloneDeep(defaultShowing))
            }
            if (state.report.showings.length > target) {
                state.report.showings.splice(target)
            }
        },
        addBuyer(state) {
            if (!state.report.buyers) {
                state.report.buyers = []
            }
            state.report.buyers.push(_cloneDeep(defaultBuyer))
        },
        mergeSuggestedBuyers(state, buyers) {
            if (!state.report.buyers) {
                state.report.buyers = []
            }

            const existingIds = new Set(
                (state.report.buyers || [])
                    .map((buyer) => buyer && buyer.client_id)
                    .filter(Boolean)
            )
            const incoming = (buyers || []).filter((buyer) =>
                buyer && buyer.client_id && !existingIds.has(buyer.client_id)
            )

            if (incoming.length) {
                state.report.buyers = incoming.concat(state.report.buyers)
            }
        },
        removeBuyer(state, index) {
            if (state.report.buyers && state.report.buyers[index]) {
                state.report.buyers.splice(index, 1)
            }
        },
        addExclusive(state) {
            if (!state.report.exclusives) {
                state.report.exclusives = []
            }
            state.report.exclusives.push(_cloneDeep(defaultExclusive))
        },
        removeExclusive(state, index) {
            if (state.report.exclusives && state.report.exclusives[index]) {
                state.report.exclusives.splice(index, 1)
            }
        },
        addDeposit(state) {
            if (!state.report.deposits) {
                state.report.deposits = []
            }
            state.report.deposits.push(_cloneDeep(defaultDeposit))
        },
        removeDeposit(state, index) {
            if (state.report.deposits && state.report.deposits[index]) {
                state.report.deposits.splice(index, 1)
            }
        },
        addPlannedTask(state, task) {
            if (!state.report.planned_tasks) {
                state.report.planned_tasks = []
            }
            state.report.planned_tasks.push(_cloneDeep(task || defaultPlannedTask))
        },
        removePlannedTask(state, index) {
            if (state.report.planned_tasks && state.report.planned_tasks[index]) {
                state.report.planned_tasks.splice(index, 1)
            }
        },
    },
    
    actions: {
        setReports(context, payload) {
            context.commit('setReports', payload);
        },
        setReport(context, payload) {
            context.commit('setReport', payload);
        },
        setReportsCount(context, payload) {
            context.commit('setReportsCount', payload);
        },
        setAllUsers(context, payload) {
            context.commit('setAllUsers', payload);
        },
        setFilters(context, payload) {
            context.commit('setFilters', payload);
        },
        resetFilters(context) {
            context.commit('resetFilters');
        },
        addOwnerMeeting(context) {
            context.commit('addOwnerMeeting');
        },
        removeOwnerMeeting(context, index) {
            context.commit('removeOwnerMeeting', index);
        },
        addShowing(context) {
            context.commit('addShowing');
        },
        removeShowing(context, index) {
            context.commit('removeShowing', index);
        },
        syncShowingsToCount(context, count) {
            context.commit('syncShowingsToCount', count);
        },
        addBuyer(context) {
            context.commit('addBuyer');
        },
        mergeSuggestedBuyers(context, buyers) {
            context.commit('mergeSuggestedBuyers', buyers);
        },
        removeBuyer(context, index) {
            context.commit('removeBuyer', index);
        },
        addExclusive(context) {
            context.commit('addExclusive');
        },
        removeExclusive(context, index) {
            context.commit('removeExclusive', index);
        },
        addDeposit(context) {
            context.commit('addDeposit');
        },
        removeDeposit(context, index) {
            context.commit('removeDeposit', index);
        },
        addPlannedTask(context, task) {
            context.commit('addPlannedTask', task);
        },
        removePlannedTask(context, index) {
            context.commit('removePlannedTask', index);
        },
        setTodayReportExists(context, payload) {
            context.commit('setTodayReportExists', payload);
        },
        async checkTodayReport({ commit }) {
            try {
                const response = await Vue.axios.get('reports/check/today');
                const exists = response && response.data ? !!response.data.exists : false;
                commit('setTodayReportExists', exists);
            } catch (error) {
                // У разі помилки не ламаємо UI, просто вважаємо, що звіту немає
                commit('setTodayReportExists', false);
                // Додаткові помилки вже обробляються глобальними інтерцепторами axios
            }
        }
    }
}

export default ReportsModule;
